import React from "react";

import { useFormFieldContext } from "~/components/form/use-form-field-context";
import { type VariantProp, cv } from "~/tools/class-variance";
import { useScoped } from "~/tools/impulse";
import { px } from "~/tools/px";

import styles from "./promql-input.module.css";
import { type UseCodeMirrorParams, useCodeMirror } from "./useCodeMirror";

const variants = cv("rounded-sm", {
	variants: {
		size: {
			sm: styles.sm,
			md: styles.md,
			lg: styles.lg,
		},
		invalid: {
			true: styles.invalid,
		},
	},
});

export interface PromQLInputProps extends UseCodeMirrorParams {
	/**
	 * @default false
	 */
	autoFocus?: boolean;

	/**
	 * The minimum height in pixels
	 *
	 * @default 0
	 */
	minHeight?: number;

	className?: string;

	/**
	 * @default "md"
	 */
	size?: VariantProp<typeof variants, "size">;
}

// https://github.com/prometheus/prometheus/blob/431ea75a11ca165dad9dd5d629b3cf975f4c186b/web/ui/react-app/src/pages/graph/CMExpressionInput.tsx
export const PromQLInput: React.FC<PromQLInputProps> = ({
	value,
	autoFocus = false,
	minHeight = 0,
	size = "md",
	onChange,
	onExecute,
	onBlur,
	onFocus,
	readonly,
	lint,
	className,
	completionTypes,
	...rest
}) => {
	const containerRef = React.useRef<HTMLDivElement>(null);
	// due to async initialization of the CodeMirror editor
	// it should be possible to schedule focus while the editor is not yet ready
	const [isFocusScheduled, setIsFocusScheduled] = React.useState(false);

	const formField = useFormFieldContext();
	const submitting = useScoped((scope) => formField?.state.isSubmitting(scope), [formField?.state]);
	const invalid = useScoped((scope) => formField?.state.isInvalid(scope), [formField?.state]);

	const view = useCodeMirror(containerRef, {
		onChange,
		onFocus,
		onExecute,
		onBlur: () => {
			formField?.state.setTouched(true);
			onBlur?.();
		},
		value,
		lint,
		readonly: readonly || submitting,
		completionTypes,
	});

	React.useEffect(() => {
		if (view && (autoFocus || isFocusScheduled)) {
			const transaction = view.state.update({
				// moves the cursor at the end of autoFocus
				selection: {
					anchor: view.state.doc.length,
				},
			});

			view.focus();
			view.dispatch(transaction);
			// CodeMirror uses contenteditable API so it does not scroll automatically into the view on focus
			containerRef.current?.scrollIntoView();
			setIsFocusScheduled(false);
		}
	}, [view, autoFocus, isFocusScheduled]);

	React.useEffect(() => formField?.state.onFocusWhenInvalid(() => setIsFocusScheduled(true)), [formField?.state]);

	React.useEffect(() => {
		if (view) {
			view.scrollDOM.style.minHeight = px(minHeight);
			view.contentDOM.style.minHeight = px(minHeight);
		}
	}, [view, minHeight]);

	return <div ref={containerRef} spellCheck={false} {...rest} className={variants({ size, invalid, className })} />;
};
